import { useState } from 'react';
import axios from 'axios';
import { 
  Bot, Sparkles, Mail, FileText, MessageSquare, 
  Loader2, Copy, Check, AlertTriangle, X
} from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AIAssistant = ({ contactId, contactName, dealId, onClose, getAuthHeaders }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  
  // Pour l'email
  const [emailIntent, setEmailIntent] = useState('relance');
  const [emailTone, setEmailTone] = useState('professionnel');
  
  // Pour les notes de réunion
  const [meetingNotes, setMeetingNotes] = useState('');

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    toast.success('Copié dans le presse-papier');
    setTimeout(() => setCopied(false), 2000);
  };

  const generateSummary = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/ai/contact-summary`,
        { contact_id: contactId },
        getAuthHeaders()
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération');
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
    }
  };

  const generateActions = async () => {
    if (!dealId) {
      toast.error('Aucun deal associé à ce contact');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/ai/deal-next-actions`,
        { deal_id: dealId },
        getAuthHeaders()
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération');
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
    }
  };

  const generateEmail = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/ai/draft-email`,
        { 
          contact_id: contactId,
          deal_id: dealId,
          intent: emailIntent,
          tone: emailTone
        },
        getAuthHeaders()
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de la génération');
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
    }
  };

  const analyzeMeetingNotes = async () => {
    if (!meetingNotes.trim()) {
      toast.error('Veuillez entrer des notes de réunion');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const response = await axios.post(
        `${API}/ai/meeting-notes-to-interaction`,
        { 
          contact_id: contactId,
          raw_notes: meetingNotes
        },
        getAuthHeaders()
      );
      setResult(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || 'Erreur lors de l\'analyse');
      toast.error(err.response?.data?.detail || 'Erreur IA');
    } finally {
      setLoading(false);
    }
  };

  const tabs = [
    { id: 'summary', label: 'Résumé', icon: FileText },
    { id: 'actions', label: 'Actions', icon: Sparkles },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'notes', label: 'Notes réunion', icon: MessageSquare }
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="p-4 border-b flex items-center justify-between" style={{ background: '#673AB7' }}>
          <div className="flex items-center gap-2 text-white">
            <Bot size={24} />
            <h2 className="text-lg font-bold">Assistant IA - {contactName}</h2>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded p-1">
            <X size={20} />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); setResult(null); setError(null); }}
              className={`flex-1 py-3 px-4 flex items-center justify-center gap-2 text-sm font-medium transition-colors ${
                activeTab === tab.id 
                  ? 'border-b-2 text-purple-600' 
                  : 'text-gray-500 hover:text-gray-700'
              }`}
              style={activeTab === tab.id ? { borderBottomColor: '#673AB7' } : {}}
            >
              <tab.icon size={16} />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="p-4 max-h-[60vh] overflow-y-auto">
          {/* Tab: Résumé */}
          {activeTab === 'summary' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Génère un résumé complet du contact avec profil, points forts, objections et recommandations.
              </p>
              <button
                onClick={generateSummary}
                disabled={loading}
                className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white disabled:opacity-50"
                style={{ background: '#673AB7' }}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                Générer le résumé
              </button>
            </div>
          )}

          {/* Tab: Actions */}
          {activeTab === 'actions' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Obtiens les 3 prochaines actions prioritaires pour faire avancer ce deal.
              </p>
              {!dealId && (
                <div className="p-3 rounded-lg flex items-center gap-2" style={{ background: '#FFF3E0' }}>
                  <AlertTriangle size={18} style={{ color: '#E65100' }} />
                  <span className="text-sm" style={{ color: '#E65100' }}>
                    Créez d'abord un deal pour ce contact pour utiliser cette fonction.
                  </span>
                </div>
              )}
              <button
                onClick={generateActions}
                disabled={loading || !dealId}
                className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white disabled:opacity-50"
                style={{ background: '#673AB7' }}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Sparkles size={18} />}
                Suggérer les actions
              </button>
            </div>
          )}

          {/* Tab: Email */}
          {activeTab === 'email' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Génère un brouillon d'email personnalisé pour ce contact.
              </p>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Objectif</label>
                  <select
                    value={emailIntent}
                    onChange={(e) => setEmailIntent(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="relance">Relance soumission</option>
                    <option value="portes_ouvertes">Suite portes ouvertes</option>
                    <option value="prise_rdv">Prise de rendez-vous</option>
                    <option value="remerciement">Remerciement</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Ton</label>
                  <select
                    value={emailTone}
                    onChange={(e) => setEmailTone(e.target.value)}
                    className="w-full p-2 border rounded"
                  >
                    <option value="professionnel">Professionnel</option>
                    <option value="amical">Amical</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
              </div>
              <button
                onClick={generateEmail}
                disabled={loading}
                className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white disabled:opacity-50"
                style={{ background: '#673AB7' }}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <Mail size={18} />}
                Générer l'email
              </button>
            </div>
          )}

          {/* Tab: Notes réunion */}
          {activeTab === 'notes' && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600">
                Collez vos notes de réunion brutes et l'IA les transformera en interaction structurée.
              </p>
              <textarea
                value={meetingNotes}
                onChange={(e) => setMeetingNotes(e.target.value)}
                placeholder="Ex: Appel avec le client. Intéressé par le modèle UHA 12x40. Budget 300k confirmé. Terrain validé. Questions sur le financement..."
                className="w-full p-3 border rounded-lg h-32 text-sm"
              />
              <button
                onClick={analyzeMeetingNotes}
                disabled={loading || !meetingNotes.trim()}
                className="w-full py-3 rounded-lg flex items-center justify-center gap-2 text-white disabled:opacity-50"
                style={{ background: '#673AB7' }}
              >
                {loading ? <Loader2 className="animate-spin" size={18} /> : <MessageSquare size={18} />}
                Analyser les notes
              </button>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="mt-4 p-3 rounded-lg" style={{ background: '#FFEBEE' }}>
              <p className="text-sm" style={{ color: '#C62828' }}>{error}</p>
            </div>
          )}

          {/* Result Display */}
          {result && (
            <div className="mt-4 p-4 rounded-lg border relative" style={{ background: '#F5F5F5' }}>
              <button
                onClick={() => handleCopy(
                  result.summary || result.recommended_actions || result.email_draft || JSON.stringify(result.analysis, null, 2)
                )}
                className="absolute top-2 right-2 p-2 rounded hover:bg-gray-200"
                title="Copier"
              >
                {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
              </button>
              
              <div className="pr-10">
                {/* Résumé contact */}
                {result.summary && (
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm font-sans" style={{ color: '#333' }}>
                      {result.summary}
                    </pre>
                  </div>
                )}
                
                {/* Actions recommandées */}
                {result.recommended_actions && (
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm font-sans" style={{ color: '#333' }}>
                      {result.recommended_actions}
                    </pre>
                  </div>
                )}
                
                {/* Email draft */}
                {result.email_draft && (
                  <div className="prose prose-sm max-w-none">
                    <pre className="whitespace-pre-wrap text-sm font-sans" style={{ color: '#333' }}>
                      {result.email_draft}
                    </pre>
                  </div>
                )}
                
                {/* Meeting notes analysis */}
                {result.analysis && (
                  <div className="space-y-3">
                    {result.analysis.summary && (
                      <div>
                        <h4 className="font-semibold text-sm mb-1">Résumé</h4>
                        <p className="text-sm">{result.analysis.summary}</p>
                      </div>
                    )}
                    {result.analysis.key_points && (
                      <div>
                        <h4 className="font-semibold text-sm mb-1">Points clés</h4>
                        <ul className="text-sm list-disc pl-4">
                          {result.analysis.key_points.map((point, i) => (
                            <li key={i}>{point}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.analysis.next_steps && (
                      <div>
                        <h4 className="font-semibold text-sm mb-1">Prochaines étapes</h4>
                        <ul className="text-sm list-disc pl-4">
                          {result.analysis.next_steps.map((step, i) => (
                            <li key={i}>{step}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {result.analysis.sentiment && (
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold">Sentiment:</span>
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                          result.analysis.sentiment === 'positif' ? 'bg-green-100 text-green-800' :
                          result.analysis.sentiment === 'négatif' ? 'bg-red-100 text-red-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {result.analysis.sentiment}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
              
              <p className="text-xs mt-3" style={{ color: '#999' }}>
                Généré le {new Date(result.generated_at).toLocaleString('fr-CA')}
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50">
          <p className="text-xs text-center" style={{ color: '#999' }}>
            Propulsé par GPT-4o • Les réponses sont générées par IA et doivent être vérifiées
          </p>
        </div>
      </div>
    </div>
  );
};

export default AIAssistant;
